import { type DependencyList, type RefObject, useCallback, useLayoutEffect, useState } from 'react';

import { MIN_TABLE_SCROLL_HEIGHT } from 'utils/constants';

const BOTTOM_PADDING = 16;

export const useTableScrollHeight = (
  tableRef: RefObject<HTMLElement>,
  deps: DependencyList = []
) => {
  const [scrollHeight, setScrollHeight] = useState<number>();

  const calculateScrollHeight = useCallback(() => {
    const tableContainer = tableRef.current;
    if (!tableContainer) {
      return;
    }

    const { top } = tableContainer.getBoundingClientRect();

    const cardHeader = tableContainer.querySelector<HTMLElement>('.ant-card-head');
    const tableHeader = tableContainer.querySelector<HTMLElement>('.ant-table-thead');
    const pagination = tableContainer.querySelector<HTMLElement>('.ant-pagination');

    const paginationStyle = pagination ? window.getComputedStyle(pagination) : null;
    const paginationMargin = paginationStyle
      ? parseFloat(paginationStyle.marginTop) + parseFloat(paginationStyle.marginBottom)
      : 0;

    const availableMaxHeight = Math.max(
      MIN_TABLE_SCROLL_HEIGHT,
      window.innerHeight -
        top -
        (cardHeader?.offsetHeight ?? 0) -
        (tableHeader?.offsetHeight ?? 0) -
        (pagination?.offsetHeight ?? 0) -
        paginationMargin -
        BOTTOM_PADDING
    );

    // Measure the rendered table height.
    const table =
      tableContainer.querySelector<HTMLElement>('.ant-table-content table') ??
      tableContainer.querySelector<HTMLElement>('.ant-table-body table');

    if (!table) {
      setScrollHeight(undefined);
      return;
    }

    const contentHeight = table.offsetHeight;

    setScrollHeight(contentHeight > availableMaxHeight ? availableMaxHeight : undefined);
  }, [tableRef]);

  useLayoutEffect(() => {
    const tableContainer = tableRef.current;
    if (!tableContainer) {
      return;
    }

    calculateScrollHeight();

    const resizeObserver = new ResizeObserver(() => {
      calculateScrollHeight();
    });

    // Observe the card body so we catch table mount, data changes, pagination, etc.
    const observedElement = tableContainer.querySelector('.ant-card-body') ?? tableContainer;

    resizeObserver.observe(observedElement);

    window.addEventListener('resize', calculateScrollHeight);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', calculateScrollHeight);
    };
    // Caller-supplied deps (e.g. `loading`) extend this array so layout is remeasured
    // after async table updates. tableRef is stable; calculateScrollHeight already reads it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculateScrollHeight, ...deps]);

  return scrollHeight;
};
